import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getGroups } from '../../actions/groups';
import { getResources, getResourceCategories } from '../../actions/resources';
import { Grid, CircularProgress, Typography, Divider, TextField, Paper, InputAdornment, Tooltip, Card, CardContent, Chip, Button, Rating, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemIcon, ListItemText, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';
import ArticleIcon from '@mui/icons-material/Article';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SchoolIcon from '@mui/icons-material/School';
import BuildIcon from '@mui/icons-material/Build';
import HelpIcon from '@mui/icons-material/Help';
import AlertIcon from '@mui/icons-material/Warning';
import StarIcon from '@mui/icons-material/Star';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LaunchIcon from '@mui/icons-material/Launch';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import { useAuth } from '../../contexts/AuthContext';
import LoginModal from '../Auth/LoginModal';

const StyledContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  padding: '24px 32px',
  width: '100%',
  minHeight: 'calc(100vh - 64px)',
  backgroundColor: '#F0EFED',
});

const GroupsSection = styled('div')({
  display: 'flex',
  gap: '12px',
  overflowX: 'auto',
  padding: '8px 0',
  marginBottom: '32px',
  '&::-webkit-scrollbar': {
    height: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '10px',
    '&:hover': {
      background: '#666',
    },
  },
});

const GroupChip = styled('div')(({ active, color }) => ({
  padding: '8px 16px',
  borderRadius: '8px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  backgroundColor: active ? color || '#667eea' : '#fff',
  color: active ? '#fff' : '#333',
  border: `1px solid ${color || '#667eea'}`,
  transition: 'all 0.2s ease',
  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
  },
}));

const SpecialGroupChip = styled('div')({
  padding: '8px 16px',
  borderRadius: '8px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  backgroundColor: '#fff',
  color: '#dc3545', // Red color for emphasis
  border: '1px solid #dc3545',
  transition: 'all 0.2s ease',
  boxShadow: '0 2px 4px rgba(220, 53, 69, 0.1)',
  marginLeft: 'auto', // Push to the end of the group list
  fontWeight: 500,
  '&:hover': {
    backgroundColor: '#dc3545',
    color: '#fff',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 8px rgba(220, 53, 69, 0.2)',
  },
  '&.active': {
    backgroundColor: '#dc3545',
    color: '#fff',
  }
});

const ResourcesGrid = styled(Grid)({
  marginTop: '24px',
});

const ResourceCard = styled('div')({
  backgroundColor: '#fff',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  transition: 'transform 0.2s, box-shadow 0.2s',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
  }
});

const ResourceImage = styled('div')({
  height: '160px',
  backgroundColor: '#667eea',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
});

const ResourceContent = styled('div')({
  padding: '20px',
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
});

const ResourceLink = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 24px',
  borderRadius: '8px',
  color: '#fff',
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: '0.9rem',
  transition: 'opacity 0.2s',
  marginTop: 'auto',
  marginLeft: 'auto',
  marginRight: 'auto',
  textAlign: 'center',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  whiteSpace: 'nowrap',
  minWidth: 'fit-content',
  maxWidth: '90%',
  '&:hover': {
    opacity: 0.9
  }
});

const PageTitle = styled(Typography)({
  marginBottom: '24px',
  fontWeight: 600,
  fontFamily: '"Literata", Georgia, serif',
  color: '#4A3428',
  display: 'inline-block',
});

const CategoriesGrid = styled(Grid)({
  marginTop: '24px',
  marginBottom: '32px'
});

const CategoryCard = styled(Paper)(({ theme, active, color }) => ({
  padding: '20px',
  cursor: 'pointer',
  height: '100%',
  transition: 'all 0.3s ease',
  border: active ? `2px solid ${color || '#4A3428'}` : '2px solid transparent',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
  }
}));

const CategoryHeader = styled('div')(({ color }) => ({
  padding: '16px 20px',
  background: color || 'linear-gradient(135deg, #4A3428 0%, #8B6F5C 100%)',
  display: 'flex',
  alignItems: 'center',
  gap: '12px'
}));

const ResourceHeader = styled('div')(({ color }) => ({
  padding: '16px 20px',
  background: color || 'linear-gradient(135deg, #4A3428 0%, #8B6F5C 100%)',
  display: 'flex',
  alignItems: 'center',
  gap: '12px'
}));

const ResourceIcon = styled('div')({
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff'
});

const ResourceTitle = styled(Typography)({
  color: '#fff',
  fontWeight: 500,
  fontSize: '1.1rem',
  lineHeight: 1.3,
  flex: 1
});

const ResourceDescription = styled(Typography)({
  color: '#666',
  marginBottom: '20px',
  flex: 1,
  display: '-webkit-box',
  '-webkit-line-clamp': '3',
  '-webkit-box-orient': 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: '1.5em',
  maxHeight: '4.5em', // 3 lines * 1.5em line-height
});

const BackButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 16px',
  backgroundColor: 'transparent',
  border: '1px solid #667eea',
  borderRadius: '8px',
  color: '#667eea',
  cursor: 'pointer',
  marginBottom: '24px',
  transition: 'all 0.2s ease',
  fontFamily: 'inherit', // Inherit font from parent
  fontSize: '0.875rem',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: '#f0f2ff',
    transform: 'translateX(-4px)',
  },
  '&:focus': {
    outline: 'none',
    boxShadow: '0 0 0 2px rgba(102, 126, 234, 0.2)',
  }
});

const CommissionChip = styled(Chip)({
  backgroundColor: '#4caf50',
  color: '#fff',
  fontWeight: 600,
  fontSize: '0.9rem',
});

const CategoryChip = styled(Chip)(({ category }) => {
  const colors = {
    therapy: '#2196f3',
    courses: '#ff9800',
    products: '#9c27b0',
    books: '#f44336',
    apps: '#00bcd4',
    other: '#607d8b'
  };
  
  return {
    backgroundColor: colors[category] || '#607d8b',
    color: '#fff',
    fontWeight: 500,
  };
});

const FilterSection = styled(Box)({
  display: 'flex',
  gap: '16px',
  marginBottom: '24px',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'center',
});

const getResourceIcon = (type) => {
  switch (type?.toLowerCase()) {
    case 'link':
      return <LinkIcon />;
    case 'document':
      return <ArticleIcon />;
    case 'video':
      return <PlayCircleIcon />;
    case 'book':
      return <MenuBookIcon />;
    case 'course':
      return <SchoolIcon />;
    case 'tool':
      return <BuildIcon />;
    case 'affiliate':
      return <MonetizationOnIcon />;
    default:
      return <LinkIcon />;
  }
};

const Resources = () => {
  const dispatch = useDispatch();
  const groups = useSelector((state) => state?.groups) || [];
  const { resources = [], categories = [], loading } = useSelector((state) => state?.resources) || { resources: [], categories: [], loading: true };
  
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategories, setShowCategories] = useState(true);
  const [showProfessionalHelp, setShowProfessionalHelp] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    dispatch(getGroups());
    dispatch(getResources());
    dispatch(getResourceCategories());
  }, [dispatch]);

  // Scroll effect: always show login modal for unauthenticated users
  useEffect(() => {
    if (!isAuthenticated) {
      const handleScroll = () => {
        if (window.scrollY > 100) {
          setShowLoginModal(true);
        }
      };
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [isAuthenticated]);

  const handleResourceInteraction = () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return false;
    }
    return true;
  };

  const handleLoginModalClose = () => {
    setShowLoginModal(false);
  };

  const handleLoginSuccess = (user) => {
    setShowLoginModal(false);
    // Optionally refresh the page or redirect
    window.location.reload();
  };

  const handleGroupClick = (group) => {
    if (!handleResourceInteraction()) return;
    
    setSelectedGroup(group);
    setSelectedCategory(null);
    setShowCategories(true);
    setShowProfessionalHelp(false);
  };

  const handleCategoryClick = (category) => {
    if (!handleResourceInteraction()) return;
    
    setSelectedCategory(category);
    setShowCategories(false);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setShowCategories(true);
  };

  const handleProfessionalHelpClick = () => {
    if (!handleResourceInteraction()) return;
    
    setShowProfessionalHelp(true);
    setSelectedGroup(null);
    setSelectedCategory(null);
  };

  const handleProgramClick = (program) => {
    if (!handleResourceInteraction()) return;
    
    setSelectedProgram(program);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedProgram(null);
  };

  const handleVisitProgram = (url) => {
    if (!handleResourceInteraction()) return;
    
    const trackingUrl = new URL(url);
    window.open(trackingUrl.toString(), '_blank', 'noopener,noreferrer');
  };

  // Filter resources based on selected group, category and search query
  const filteredResources = (resources || []).filter(resource => {
    const matchesSearch = !searchQuery || 
      resource.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.description?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesGroup = !selectedGroup || 
      resource.group === selectedGroup._id || 
      resource.group?._id === selectedGroup._id;

    const matchesCategory = !selectedCategory || 
      resource.category === selectedCategory._id ||
      resource.category?._id === selectedCategory._id;

    return matchesSearch && matchesGroup && matchesCategory;
  });

  // Filter categories based on selected group
  const filteredCategories = (categories || []).filter(category => {
    if (!selectedGroup) return true; // Show all categories when no group is selected
    return category.groupId?._id === selectedGroup._id || category.groupId === selectedGroup._id;
  });


  // Helper function to get group color for a category
  const getCategoryGroupColor = (category) => {
    // If category has populated groupId
    if (category.groupId?.color) {
      return category.groupId.color;
    }
    
    // If we need to find the group manually
    const parentGroup = groups.find(group => 
      group._id === category.groupId || group._id === category.groupId?._id
    );
    return parentGroup?.color || 'linear-gradient(135deg, #4A3428 0%, #8B6F5C 100%)';
  };

  // Add this helper function
  const getResourceGroupColor = (resource) => {
    // If resource has populated group
    if (resource.group?.color) {
      return resource.group.color;
    }
    
    // If we need to find the group manually
    const resourceGroup = groups.find(group => 
      group._id === resource.group || group._id === resource.group?._id
    );
    return resourceGroup?.color || 'linear-gradient(135deg, #4A3428 0%, #8B6F5C 100%)';
  };

  // Helper function to render affiliate program content
  const renderAffiliateContent = (resource) => {
    if (resource.type !== 'affiliate') return null;
    // Only show rating and review count for users
    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Rating value={resource.content.rating} readOnly size="small" />
          <Typography variant="body2" color="textSecondary">
            ({resource.content.reviewCount} reviews)
          </Typography>
        </Box>
      </Box>
    );
  };

  // Early return if Redux state is not properly initialized
  if (!groups || !Array.isArray(groups) || !resources || !Array.isArray(resources)) {
    return (
      <StyledContainer>
        <CircularProgress />
      </StyledContainer>
    );
  }

  if (loading || !groups || !resources) {
    return (
      <StyledContainer>
        <CircularProgress />
      </StyledContainer>
    );
  }

  if (!groups?.length && !resources?.length) {
    return (
      <StyledContainer>
        <CircularProgress />
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      <PageTitle variant="h5">
        Resource Library
      </PageTitle>                                                                                    

      <GroupsSection>
        {groups.map((group) => (
          <GroupChip
            key={group._id}
            active={selectedGroup?._id === group._id}
            color={group.color}
            onClick={() => handleGroupClick(group)}
          >
            {group.name}
          </GroupChip>
        ))}
        <SpecialGroupChip
          className={showProfessionalHelp ? 'active' : ''}
          onClick={handleProfessionalHelpClick}
        >
          Professional Help
        </SpecialGroupChip>
      </GroupsSection>

      {!showProfessionalHelp && (
        <>
          {!showCategories && (
            <BackButton onClick={handleBackToCategories}>
              <ArrowBackIcon />
              Back to Categories
            </BackButton>
          )}

          {showCategories ? (
            <CategoriesGrid container spacing={3}>
              {filteredCategories.length > 0 ? (
                filteredCategories.map((category) => (
                  <Grid item xs={12} sm={6} md={4} key={category._id}>
                    <CategoryCard onClick={() => handleCategoryClick(category)}>
                      <CategoryHeader color={getCategoryGroupColor(category)}>
                        <ResourceIcon>
                          <SchoolIcon />
                        </ResourceIcon>
                        <ResourceTitle>
                          {category.name}
                        </ResourceTitle>
                      </CategoryHeader>
                      <ResourceContent>
                        <Tooltip title={category.description} placement="top">
                          <ResourceDescription>
                            {category.description}
                          </ResourceDescription>
                        </Tooltip>
                        <Typography 
                          variant="subtitle1"
                          sx={{ 
                            color: getCategoryGroupColor(category),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            fontSize: '1rem',
                            fontWeight: 500,
                            marginTop: 'auto',
                            width: '100%',
                            textAlign: 'center',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          View Resources
                        </Typography>
                      </ResourceContent>
                    </CategoryCard>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Typography variant="body1" color="textSecondary" align="center">
                    No categories found {selectedGroup ? `for ${selectedGroup.name}` : ''}.
                  </Typography>
                </Grid>
              )}
            </CategoriesGrid>
          ) : (
            <>
              <Typography variant="h6" sx={{ mb: 3 }}>
                {selectedCategory?.name} Resources
              </Typography>
              
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  maxWidth: '300px',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  marginBottom: '24px',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.1)',
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />

              <ResourcesGrid container spacing={3}>
                {loading ? (
                  <Grid item xs={12}>
                    <CircularProgress />
                  </Grid>
                ) : filteredResources.length > 0 ? (
                  filteredResources.map((resource) => (
                    <Grid item xs={12} sm={6} md={4} key={resource._id}>
                      <ResourceCard 
                        onClick={() => {
                          if (!handleResourceInteraction()) return;
                          
                          if (resource.type === 'affiliate') {
                            handleProgramClick(resource);
                          } else {
                            window.open(resource.content.url, '_blank', 'noopener,noreferrer');
                          }
                        }}
                      >
                        <ResourceHeader color={getResourceGroupColor(resource)}>
                          <ResourceIcon>
                            {getResourceIcon(resource.type)}
                          </ResourceIcon>
                          <ResourceTitle>
                            {resource.title}
                          </ResourceTitle>
                          <Box sx={{ display: 'flex', gap: 1, marginLeft: 'auto' }}>
                            {resource.content?.userDiscount && (
                              <Box sx={{ 
                                backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                                color: '#fff',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '0.7rem',
                                fontWeight: 600
                              }}>
                                {resource.content.userDiscount}
                              </Box>
                            )}
                            {resource.featured && (
                              <Box sx={{ 
                                backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                                color: '#fff',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '0.7rem',
                                fontWeight: 600
                              }}>
                                Featured
                              </Box>
                            )}
                          </Box>
                        </ResourceHeader>
                        <ResourceContent>
                          <Tooltip title={resource.description} placement="top">
                            <ResourceDescription>
                              {resource.description}
                            </ResourceDescription>
                          </Tooltip>
                          
                          {/* Render affiliate-specific content */}
                          {resource.type === 'affiliate' && renderAffiliateContent(resource)}
                          
                          <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                            {resource.type === 'affiliate' ? (
                              <>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!handleResourceInteraction()) return;
                                    handleProgramClick(resource);
                                  }}
                                  fullWidth
                                >
                                  View Details
                                </Button>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!handleResourceInteraction()) return;
                                    handleVisitProgram(resource.content.affiliateUrl);
                                  }}
                                  startIcon={<LaunchIcon />}
                                  fullWidth
                                >
                                  Visit Program
                                </Button>
                              </>
                            ) : (
                              <ResourceLink 
                                href={resource.content.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                  if (!handleResourceInteraction()) {
                                    e.preventDefault();
                                    return;
                                  }
                                }}
                              >
                                View Resource
                              </ResourceLink>
                            )}
                          </Box>
                        </ResourceContent>
                      </ResourceCard>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Typography variant="body1" color="textSecondary" align="center">
                      No resources found 
                      {selectedCategory ? ` in ${selectedCategory.name}` : ''}.
                    </Typography>
                  </Grid>
                )}
              </ResourcesGrid>
            </>
          )}
        </>
      )}

      {showProfessionalHelp && (
        <ResourcesGrid container spacing={3}>
          {professionalHelpResources.map((resource, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <ResourceCard>
                <ResourceHeader 
                  style={{ 
                    backgroundColor: resource.emergency ? '#dc3545' : undefined
                  }}
                >
                  <ResourceIcon>
                    {resource.emergency ? <AlertIcon /> : <HelpIcon />}
                  </ResourceIcon>
                  <ResourceTitle>
                    {resource.title}
                  </ResourceTitle>
                </ResourceHeader>
                <ResourceContent>
                  <ResourceDescription>
                    {resource.description}
                  </ResourceDescription>
                  <ResourceLink 
                    href={resource.content} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{
                      background: resource.emergency ? 
                        'linear-gradient(135deg, #dc3545 0%, #c82333 100%)' :
                        undefined
                    }}
                    onClick={(e) => {
                      if (!handleResourceInteraction()) {
                        e.preventDefault();
                        return;
                      }
                    }}
                  >
                    {resource.emergency ? 'Get Help Now' : 'View Resource'}
                  </ResourceLink>
                </ResourceContent>
              </ResourceCard>
            </Grid>
          ))}
        </ResourcesGrid>
      )}

      {/* Program Details Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        {selectedProgram && selectedProgram.type === 'affiliate' && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {selectedProgram.title}
                </Typography>
                {selectedProgram.featured && (
                  <Chip label="Featured" color="success" size="small" />
                )}
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {selectedProgram.description}
              </Typography>

              {selectedProgram.content?.userDiscount && (
                <Box sx={{ 
                  display: 'inline-block',
                  backgroundColor: 'success.main',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  marginBottom: 2,
                  fontWeight: 600
                }}>
                  {selectedProgram.content.userDiscount}
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Rating value={selectedProgram.content.rating} readOnly size="small" />
                <Typography variant="body2" color="textSecondary">
                  ({selectedProgram.content.reviewCount} reviews)
                </Typography>
              </Box>

              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                What You'll Get
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {selectedProgram.content.features.slice(0, 4).map((feature, index) => (
                  <Chip key={index} label={feature} size="small" variant="outlined" color="primary" />
                ))}
              </Box>

              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                Provided by {selectedProgram.content.provider}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Close</Button>
              <Button 
                variant="contained" 
                onClick={() => handleVisitProgram(selectedProgram.content.affiliateUrl)}
                startIcon={<LaunchIcon />}
              >
                Visit Program
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Login Modal */}
      <LoginModal
        open={showLoginModal}
        onClose={handleLoginModalClose}
        onLoginSuccess={handleLoginSuccess}
      />
    </StyledContainer>
  );
};

export default Resources;